import { isStandalonePwa } from '@/lib/pwaDetect';

/** Paramètres d’URL reconnus pour restaurer la session (PWA ≠ navigateur sur iOS). */
export const SESSION_TOKEN_QUERY_KEYS = ['token', 'session'] as const;

const PERSISTENCE_FLAG = '__dlSessionUrlPersistence';
const AUTH_COOKIE = 'dl_token';
const ENTRY_COOKIE = 'dl_entry';
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

/** Tant que l’app n’est pas ouverte depuis l’icône PWA : le jeton reste dans l’URL. */
export function shouldPersistSessionInUrl(): boolean {
  return typeof window !== 'undefined' && !isStandalonePwa();
}

function cookieSecureSuffix(): string {
  return typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]).trim() || null;
  } catch {
    return match[1].trim() || null;
  }
}

function writeCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${cookieSecureSuffix()}`;
}

export function getStoredSessionToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem('token')?.trim() || null;
}

/** Jeton présent dans l’URL (?token= / ?session=). */
export function readTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  for (const key of SESSION_TOKEN_QUERY_KEYS) {
    const value = url.searchParams.get(key)?.trim();
    if (value) return value;
  }
  return null;
}

/** Cookie partagé Safari → PWA (iOS isole le localStorage). */
export function readTokenFromCookie(): string | null {
  return readCookie(AUTH_COOKIE);
}

/** Dernière page utile avant installation (sans jeton dans la valeur). */
export function readPwaEntryPath(): string {
  const raw = readCookie(ENTRY_COOKIE);
  if (!raw || !raw.startsWith('/')) return '/shooting-guide?new=true';
  return raw;
}

export function persistAuthToken(token: string): void {
  const t = token.trim();
  if (!t || typeof window === 'undefined') return;
  localStorage.setItem('token', t);
  writeCookie(AUTH_COOKIE, t);
}

export function persistPwaEntryPath(path?: string): void {
  if (typeof window === 'undefined' || !shouldPersistSessionInUrl()) return;
  const source = path ?? `${window.location.pathname}${window.location.search}`;
  try {
    const url = new URL(source, window.location.origin);
    for (const key of SESSION_TOKEN_QUERY_KEYS) {
      url.searchParams.delete(key);
    }
    const search = url.searchParams.toString();
    const clean = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    writeCookie(ENTRY_COOKIE, clean);
  } catch {
    writeCookie(ENTRY_COOKIE, '/shooting-guide?new=true');
  }
}

function isJwtExpired(token: string): boolean {
  try {
    const part = token.split('.')[1];
    if (!part) return true;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

/** Efface jeton localStorage, cookies et paramètres d’URL. */
export function clearAuthSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('token');
  const expire = 'path=/; max-age=0; SameSite=Lax';
  document.cookie = `${AUTH_COOKIE}=; ${expire}${cookieSecureSuffix()}`;
  document.cookie = `${ENTRY_COOKIE}=; ${expire}${cookieSecureSuffix()}`;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of SESSION_TOKEN_QUERY_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      const search = url.searchParams.toString();
      window.history.replaceState(
        null,
        '',
        `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
      );
    }
  } catch {
    /* ignore */
  }
}

function normalizeAuthToken(token: string | null): string | null {
  if (!token) return null;
  if (isJwtExpired(token)) {
    clearAuthSession();
    return null;
  }
  return token;
}

/**
 * Jeton d’auth : URL → localStorage → cookie (expiré → null + déconnexion locale).
 * À utiliser partout à la place de localStorage.getItem('token').
 */
export function getAuthToken(): string | null {
  const fromUrl = readTokenFromUrl();
  if (fromUrl) {
    persistAuthToken(fromUrl);
    return normalizeAuthToken(fromUrl);
  }
  const stored = getStoredSessionToken();
  if (stored) return normalizeAuthToken(stored);
  const fromCookie = readTokenFromCookie();
  if (fromCookie) {
    persistAuthToken(fromCookie);
    return normalizeAuthToken(fromCookie);
  }
  return null;
}

const SESSION_SETTLE_MS = 500;
const SESSION_POLL_MS = 100;
const SESSION_MAX_WAIT_MS = 1500;

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Attend que le jeton (URL, cookie, script de boot) soit prêt avant API / redirections.
 * Évite les courses PWA → inscription ou choix des packs à tort.
 */
export async function waitForAuthSession(
  maxWaitMs = SESSION_MAX_WAIT_MS,
): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  await delay(isStandalonePwa() ? SESSION_SETTLE_MS : 200);

  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    applySessionFromUrl();
    const token = getAuthToken();
    if (token) return token;
    await delay(SESSION_POLL_MS);
  }

  return getAuthToken();
}

/** Appliqué avant React (URL + cookie → localStorage). */
export const PWA_SESSION_BOOTSTRAP_SCRIPT = `(function(){try{function rc(n){var m=document.cookie.match(new RegExp("(?:^|; )"+n+"=([^;]*)"));return m?decodeURIComponent(m[1]).trim():"";}var u=new URL(location.href);var t=(u.searchParams.get("token")||u.searchParams.get("session")||rc("dl_token")||"").trim();if(!t)return;localStorage.setItem("token",t);document.cookie="dl_token="+encodeURIComponent(t)+";path=/;max-age=604800;SameSite=Lax"+(location.protocol==="https:"?"; Secure":"");var ep=location.pathname+location.search;try{var eu=new URL(ep,location.origin);eu.searchParams.delete("token");eu.searchParams.delete("session");var eq=eu.searchParams.toString();ep=eu.pathname+(eq?"?"+eq:"");}catch(e){}document.cookie="dl_entry="+encodeURIComponent(ep)+";path=/;max-age=604800;SameSite=Lax"+(location.protocol==="https:"?"; Secure":"");var st=window.matchMedia("(display-mode: standalone)").matches||(window.navigator.standalone===true);if(st){u.searchParams.delete("token");u.searchParams.delete("session");var q=u.searchParams.toString();history.replaceState(null,"",u.pathname+(q?"?"+q:"")+u.hash);}}catch(e){}})();`;

export function appendSessionTokenToPath(path: string, token: string): string {
  const trimmed = token.trim();
  if (!trimmed) return path;

  const hashIdx = path.indexOf('#');
  const hash = hashIdx >= 0 ? path.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? path.slice(0, hashIdx) : path;
  const qIdx = withoutHash.indexOf('?');
  const pathname = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;
  const params = new URLSearchParams(qIdx >= 0 ? withoutHash.slice(qIdx + 1) : '');
  params.set('token', trimmed);
  const qs = params.toString();
  return `${pathname}?${qs}${hash}`;
}

export function pathWithSessionTokenInUrl(path: string, token?: string | null): string {
  if (!shouldPersistSessionInUrl()) return path;
  const t = token?.trim() || getAuthToken();
  if (!t) return path;
  return appendSessionTokenToPath(path, t);
}

/** @deprecated Alias */
export const pathWithMobileSessionToken = pathWithSessionTokenInUrl;

export function hrefWithSessionToken(href: string): string {
  if (!shouldPersistSessionInUrl()) return href;
  const token = getAuthToken();
  if (!token) return href;

  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return href;
  }

  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      const u = new URL(href);
      if (u.origin !== window.location.origin) return href;
      const path = `${u.pathname}${u.search}${u.hash}`;
      if (u.searchParams.get('token') === token) return href;
      return `${u.origin}${appendSessionTokenToPath(path, token)}`;
    } catch {
      return href;
    }
  }

  if (!href.startsWith('/')) return href;
  try {
    const u = new URL(href, window.location.origin);
    if (u.searchParams.get('token') === token) return href;
    return appendSessionTokenToPath(`${u.pathname}${u.search}${u.hash}`, token);
  } catch {
    return href;
  }
}

export function assignWithSessionToken(path: string): void {
  window.location.assign(hrefWithSessionToken(path));
}

export function redirectWithSessionToken(path: string): void {
  assignWithSessionToken(path);
}

export function applySessionFromUrl(): boolean {
  if (typeof window === 'undefined') return false;

  const token = readTokenFromUrl();
  if (!token) return false;

  persistAuthToken(token);
  persistPwaEntryPath();

  if (shouldPersistSessionInUrl()) {
    syncSessionTokenToUrl();
    return true;
  }

  const url = new URL(window.location.href);
  for (const key of SESSION_TOKEN_QUERY_KEYS) {
    url.searchParams.delete(key);
  }
  const search = url.searchParams.toString();
  const clean = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
  window.history.replaceState(null, '', clean);
  return true;
}

export function syncSessionTokenToUrl(): void {
  if (!shouldPersistSessionInUrl()) return;

  const token = getAuthToken();
  if (!token) return;

  persistPwaEntryPath();

  const url = new URL(window.location.href);
  if (url.searchParams.get('token') === token) return;

  url.searchParams.set('token', token);
  window.history.replaceState(null, '', url.toString());
}

export function buildPwaHandoffUrl(path?: string): string {
  if (typeof window === 'undefined') return path ?? '/shooting-guide?new=true';
  const entry =
    path ??
    (typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : readPwaEntryPath());
  const token = getAuthToken();
  const withToken = token ? appendSessionTokenToPath(entry, token) : entry;
  return `${window.location.origin}${withToken}`;
}

function augmentAnchorHrefs(root: ParentNode = document): void {
  if (!shouldPersistSessionInUrl()) return;
  root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href');
    if (!href) return;
    const next = hrefWithSessionToken(href);
    if (next !== href) anchor.setAttribute('href', next);
  });
}

export function installSessionUrlPersistence(): void {
  if (typeof window === 'undefined' || isStandalonePwa()) return;
  const w = window as Window & { [PERSISTENCE_FLAG]?: boolean };
  if (w[PERSISTENCE_FLAG]) return;
  w[PERSISTENCE_FLAG] = true;

  const run = () => {
    applySessionFromUrl();
    syncSessionTokenToUrl();
    augmentAnchorHrefs();
  };

  run();

  document.addEventListener(
    'mousedown',
    (e) => {
      const anchor = (e.target as Element | null)?.closest?.('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const next = hrefWithSessionToken(href);
      if (next !== href) anchor.setAttribute('href', next);
    },
    true,
  );

  document.addEventListener(
    'click',
    (e) => {
      const anchor = (e.target as Element | null)?.closest?.('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href) return;
      const next = hrefWithSessionToken(href);
      if (next !== href) anchor.setAttribute('href', next);
    },
    true,
  );

  const observer = new MutationObserver(() => augmentAnchorHrefs());
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('popstate', run);
  window.addEventListener('pageshow', run);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run();
  });

  const interval = window.setInterval(run, 400);

  return () => {
    window.clearInterval(interval);
    observer.disconnect();
  };
}

export function runSessionUrlSync(): void {
  applySessionFromUrl();
  syncSessionTokenToUrl();
  if (typeof document !== 'undefined') augmentAnchorHrefs();
}

/** Parse JSON API ; null si réponse HTML/texte (ex. 403 middleware). */
export async function parseApiJson<T = Record<string, unknown>>(
  response: Response,
): Promise<T | null> {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}
